import { jsPDF } from 'jspdf';

const text = (value, fallback = '—') => {
  const normalized = String(value ?? '').trim();
  return normalized || fallback;
};

const digits = (value) => String(value || '').replace(/\D/g, '');

const dateBr = (value) => {
  if (!value) return '—';
  const raw = String(value).slice(0, 10);
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) return `${match[3]}/${match[2]}/${match[1]}`;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleDateString('pt-BR');
};

const formatCpf = (value) => {
  const valueDigits = digits(value).slice(0, 11);
  return valueDigits.length === 11
    ? valueDigits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
    : text(value);
};

const formatPhone = (value) => {
  const valueDigits = digits(value).slice(0, 11);
  if (valueDigits.length === 11) return valueDigits.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  if (valueDigits.length === 10) return valueDigits.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  return text(value);
};

const money = (cents) => {
  const numeric = Number(cents);
  if (!Number.isFinite(numeric) || numeric <= 0) return '—';
  return (numeric / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const safeFilename = (value) => String(value || 'ficha-mt')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-zA-Z0-9._-]+/g, '-')
  .replace(/-+/g, '-')
  .replace(/^-|-$/g, '') || 'ficha-mt';

export function printYoungApprenticeWorldSheetPdf({
  student = {},
  employment = {},
  company = {},
  targetWindow = null,
}) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const x = 27;
  const width = pageWidth - x * 2;
  const line = [35, 35, 35];

  const phoneValues = Array.from(
    new Set([student.whatsapp, student.phone].map((value) => String(value || '').trim()).filter(Boolean)),
  );
  const companyName = company.tradeName
    || company.corporateName
    || employment.company?.tradeName
    || employment.company?.corporateName;
  const salary = employment.salaryCents || employment.scholarshipCents;
  const active = String(employment.status || 'ACTIVE').toUpperCase() === 'ACTIVE' ? 'Sim' : 'Não';

  const box = (bx, by, bw, bh) => {
    doc.setDrawColor(...line);
    doc.setLineWidth(0.25);
    doc.rect(bx, by, bw, bh);
  };

  const labelValue = (label, value, bx, by, bw, { boldValue = false, size = 10 } = {}) => {
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(size);
    doc.text(`${label}:`, bx + 1.6, by + 6.2);
    const labelWidth = doc.getTextWidth(`${label}: `);
    doc.setFont('helvetica', boldValue ? 'bold' : 'normal');
    const lines = doc.splitTextToSize(text(value), Math.max(8, bw - labelWidth - 4));
    doc.text(lines.slice(0, 2), bx + 1.6 + labelWidth, by + 6.2);
  };

  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.text('Ficha Cadastral Aprendiz - 1 via', pageWidth / 2, 20, { align: 'center' });

  let y = 31;
  const codeWidth = 58;
  box(x, y, codeWidth, 10);
  box(x + codeWidth, y, width - codeWidth, 10);
  labelValue('Código', employment.apprenticeNumber, x, y, codeWidth, { boldValue: true, size: 10.5 });
  labelValue('Nome', student.fullName, x + codeWidth, y, width - codeWidth, { boldValue: true, size: 10.5 });

  y += 12;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(`Data de Admissão:  ${dateBr(employment.admissionDate || employment.startDate)}`, x + 1.6, y + 4.5);

  y += 10;
  const personalWidths = [74, 74, width - 148];
  let px = x;
  personalWidths.forEach((bw) => {
    box(px, y, bw, 10);
    px += bw;
  });
  labelValue('CPF', formatCpf(student.cpf), x, y, personalWidths[0]);
  labelValue('RG', student.rg, x + personalWidths[0], y, personalWidths[1]);
  labelValue('Data de Nascimento', dateBr(student.birthDate), x + personalWidths[0] + personalWidths[1], y, personalWidths[2]);

  y += 10;
  box(x, y, width, 10);
  labelValue('Telefones', phoneValues.length ? phoneValues.map(formatPhone).join(' / ') : '—', x, y, width);

  y += 18;
  const bankWidth = width / 3;
  box(x, y, bankWidth, 10);
  box(x + bankWidth, y, bankWidth, 10);
  box(x + bankWidth * 2, y, width - bankWidth * 2, 10);
  labelValue('Banco', employment.bankName, x, y, bankWidth);
  labelValue('Agência', employment.bankAgency, x + bankWidth, y, bankWidth);
  labelValue('Conta', employment.bankAccount, x + bankWidth * 2, y, width - bankWidth * 2);

  y += 18;
  const startWidth = 58;
  box(x, y, width - startWidth, 16);
  box(x + width - startWidth, y, startWidth, 16);
  labelValue('Empresa', companyName, x, y, width - startWidth, { boldValue: true, size: 10.3 });
  labelValue('Início', dateBr(employment.startDate || employment.admissionDate), x + width - startWidth, y, startWidth, { size: 10 });

  y += 16;
  const workWidths = [66, width - 124, 58];
  px = x;
  workWidths.forEach((bw) => {
    box(px, y, bw, 16);
    px += bw;
  });
  labelValue('Horário', employment.workload, x, y, workWidths[0], { size: 9.7 });
  labelValue('Função', employment.role, x + workWidths[0], y, workWidths[1], { size: 9.7 });
  labelValue('NIS', student.nis, x + workWidths[0] + workWidths[1], y, workWidths[2], { size: 9.7 });

  y += 20;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10.5);
  doc.text(`Total de horas: ${text(employment.hoursPerDay)}`, x + 1.6, y);
  doc.setFont('helvetica', 'bold');
  doc.text(`Salário bruto: ${money(salary)}`, x + width - 1.6, y, { align: 'right' });

  y += 13;
  const half = width / 2;
  box(x, y, half, 10);
  box(x + half, y, width - half, 10);
  labelValue('Histórico', '—', x, y, half, { size: 9.5 });
  labelValue('Observações', employment.notes, x + half, y, width - half, { size: 9.5 });

  y += 10;
  box(x, y, half, 10);
  box(x + half, y, width - half, 10);
  labelValue('Ativo?', active, x, y, half, { size: 9.5 });
  labelValue('Data de rescisão', dateBr(employment.endDate), x + half, y, width - half, {
    boldValue: Boolean(employment.endDate),
    size: 9.5,
  });

  if (typeof doc.autoPrint === 'function') doc.autoPrint();
  const filename = `${safeFilename(`ficha-mt-${student.fullName || employment.apprenticeNumber || student.id || 'aprendiz'}`)}.pdf`;
  const url = doc.output('bloburl');

  if (targetWindow && !targetWindow.closed) {
    targetWindow.location.href = url;
    return;
  }

  const opened = typeof window !== 'undefined' ? window.open(url, '_blank') : null;
  if (!opened) doc.save(filename);
}
